import React from 'react';
import styled from 'styled-components';
import switchChangelog from './switch.changes.md';

import { useSelector } from 'react-redux';
import { HandleType, NodeToolbar, Position } from 'reactflow';
import { WarningAmberRounded } from '@mui/icons-material';
import { Stack, alpha } from '@mui/material';

import { selectCommandByNodeId, State } from '@store/index';
import { isConnectable, isValidConnection } from '@common/Node/utils';
import { Tooltip } from '@common/Tooltip';
import { NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType, SwitchNodeState } from '../../';
import { Anchor } from '../../../../../common/Node/Anchor/Anchor';
import { AnchorType } from '@components/Flow/types';
import { useTranslate } from '@common/hooks/useTranslate';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import {
  InputAnchors,
  NodeAnchorsContainer,
  NodeContainer,
  NodeErrorToolbar,
  NodeHeader,
  OutputAnchors,
} from '@common/Node';

const EmptyConditions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;

  height: 100%;
  width: 100%;

  .icon {
    display: flex;
    align-items: center;
    justify-content: center;

    width: 80px;
    height: 35px;
    border-radius: 8px;
    border: 2px dashed ${({ theme }) => alpha(theme.telnyx.orange, 0.4)};

    svg {
      color: ${({ theme }) => theme.telnyx.orange};
    }
  }

  .icon:hover {
    cursor: pointer;
  }
`;

export const config: NodeConfig = {
  version: '1.1',
  title: 'Switch',
  identifier: 'Switch',
  command_id: 'logic.switch',
  icon: 'switch',
  background: '#FF7442',
  changelog: switchChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'switch.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'switch.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
  ],
};

export const state: SwitchNodeState = {
  name: '',
  condition_groups: [],
};

export const fields: NodeField[] = [
  {
    id: 'name',
    tab: '1',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Label',
    display_name: true,
    description: 'Use this field to give your Switch node a definitive name.',
    placeholder: 'Give your Switch node a name.',
    required: false,
  },
  {
    id: 'condition_groups',
    type: NodeFieldType.Switch,
    label: 'Switch',
    description:
      'Use data from the graph to generate a boolean condition. If this condition is true, the true execution line will be executed next. Otherwise, the false execution line will be executed next.',
    required: true,
    tab: '1',
  },
];

export const output: NodeOutput = { default: null, dynamic: null };

export const Switch: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isInfiniteLoop } = useTranslate();
  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'logic.switch') return null;

  const commandState: SwitchNodeState = command.state;
  const connections = command.connections || [];
  const condition_groups = commandState.condition_groups || [];
  const targets = anchors.filter((anchor) => anchor.type === 'target');

  const displayTitle = command?.state?.name;
  const isToolbarVisible = Boolean(isNodeError);

  const dataSwitchAnchor: AnchorType = {
    label: 'Data',
    id: 'switch.output_data',
    type: 'source' as HandleType,
    variant: 'data',
  };

  const switchAnchors: AnchorType[] = [
    ...condition_groups.map((switchGroup) => {
      return {
        id: switchGroup.id,
        label: switchGroup.name,
        type: 'source' as HandleType,
        variant: 'conditional_execution',
      };
    }),
    dataSwitchAnchor,
  ];

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchorsContainer extendedSources={switchAnchors.length > 1}>
        <InputAnchors hasInputs={Boolean(targets.length)}>
          {targets.map((target) => (
            <Anchor
              key={`${target.id}${id}`}
              id={id}
              data={data}
              anchor={target}
              isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
              isConnectable={isConnectable(target, connections)}
            />
          ))}
        </InputAnchors>
        <OutputAnchors>
          {switchAnchors.length === 1 && (
            <EmptyConditions>
              <Tooltip title='No Conditions' placement='top'>
                <div className='icon'>
                  <WarningAmberRounded />
                </div>
              </Tooltip>
            </EmptyConditions>
          )}
          {switchAnchors.length > 1 &&
            switchAnchors.map((source) => (
              <Anchor
                key={`${source.id}${id}`}
                id={id}
                data={data}
                anchor={source}
                isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
                isConnectable={isConnectable(source, connections)}
              />
            ))}
        </OutputAnchors>
      </NodeAnchorsContainer>
    </NodeContainer>
  );
};

export default { config, state, fields, output };
